import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationDropdown.css';

const NotificationDropdown = () => {
    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification
    } = useNotifications();

    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'report_card_generated':
                return '📄';
            case 'new_sponsor':
                return '🤝';
            case 'new_student':
                return '👨‍🎓';
            case 'holiday':
                return '🎉';
            case 'marks_submitted':
                return '📝';
            case 'marks_approved':
                return '✅';
            case 'marks_posted':
                return '📊';
            case 'attendance_alert':
                return '📅';
            case 'new_assignment':
                return '📚';
            case 'new_enrollment':
                return '🎓';
            default:
                return '🔔';
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'report_card_generated':
                return '#10b981'; // green
            case 'new_sponsor':
                return '#3b82f6'; // blue
            case 'new_student':
                return '#8b5cf6'; // purple
            case 'holiday':
                return '#f59e0b'; // amber
            case 'marks_submitted':
                return '#06b6d4'; // cyan
            case 'marks_approved':
                return '#10b981'; // green
            case 'marks_posted':
                return '#3b82f6'; // blue
            case 'attendance_alert':
                return '#f59e0b'; // amber
            case 'new_assignment':
                return '#8b5cf6'; // purple
            case 'new_enrollment':
                return '#10b981'; // green
            default:
                return '#6b7280'; // gray
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleNotificationClick = async (notification) => {
        // Mark as read if unread
        if (!notification.is_read) {
            await markAsRead(notification.id);
        }
        // Keep dropdown open for now, you can add navigation logic later
    };

    const handleDeleteClick = async (e, notificationId) => {
        e.stopPropagation();
        await deleteNotification(notificationId);
    };

    const handleMarkAsReadClick = async (e, notificationId) => {
        e.stopPropagation();
        await markAsRead(notificationId);
    };

    const unreadNotifications = notifications.filter(n => !n.is_read);
    const readNotifications = notifications.filter(n => n.is_read);

    return (
        <div className="notification-dropdown" ref={dropdownRef}>
            <button
                className="notification-button"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className="notification-icon">🔔</span>
                {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className="notification-menu">
                    <div className="notification-header">
                        <h3>Notifications</h3>
                        {unreadCount > 0 && (
                            <button
                                className="mark-all-read-btn"
                                onClick={() => markAllAsRead()}
                            >
                                Mark all as read
                            </button>
                        )}
                    </div>

                    <div className="notification-list">
                        {notifications.length === 0 ? (
                            <div className="no-notifications">
                                <span className="no-notifications-icon">🔔</span>
                                <p>No notifications yet</p>
                            </div>
                        ) : (
                            <>
                                {/* Unread Notifications Section */}
                                {unreadNotifications.length > 0 && (
                                    <div className="notification-section">
                                        <div className="section-header">
                                            <span className="section-title">Unread ({unreadNotifications.length})</span>
                                        </div>
                                        {unreadNotifications.slice(0, 5).map((notification) => (
                                            <div
                                                key={notification.id}
                                                className="notification-item unread"
                                                onClick={() => handleNotificationClick(notification)}
                                                style={{
                                                    borderLeft: `4px solid ${getNotificationColor(notification.type)}`
                                                }}
                                            >
                                                <div className="notification-content">
                                                    <div className="notification-header-info">
                                                        <span className="notification-icon-small">
                                                            {getNotificationIcon(notification.type)}
                                                        </span>
                                                        <span className="notification-time">
                                                            {formatTime(notification.created_at)}
                                                        </span>
                                                        <div className="notification-actions">
                                                            <button
                                                                className="mark-read-btn"
                                                                onClick={(e) => handleMarkAsReadClick(e, notification.id)}
                                                                title="Mark as read"
                                                            >
                                                                ✓
                                                            </button>
                                                            <button
                                                                className="delete-notification-btn"
                                                                onClick={(e) => handleDeleteClick(e, notification.id)}
                                                                title="Delete notification"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <h4 className="notification-title">{notification.title}</h4>
                                                    <p className="notification-message">{notification.message}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Read Notifications Section */}
                                {readNotifications.length > 0 && (
                                    <div className="notification-section">
                                        <div className="section-header">
                                            <span className="section-title">Earlier ({readNotifications.length})</span>
                                        </div>
                                        {readNotifications.slice(0, 5).map((notification) => (
                                            <div
                                                key={notification.id}
                                                className="notification-item read"
                                                onClick={() => handleNotificationClick(notification)}
                                                style={{
                                                    borderLeft: `4px solid ${getNotificationColor(notification.type)}`
                                                }}
                                            >
                                                <div className="notification-content">
                                                    <div className="notification-header-info">
                                                        <span className="notification-icon-small">
                                                            {getNotificationIcon(notification.type)}
                                                        </span>
                                                        <span className="notification-time">
                                                            {formatTime(notification.created_at)}
                                                        </span>
                                                        <button
                                                            className="delete-notification-btn"
                                                            onClick={(e) => handleDeleteClick(e, notification.id)}
                                                            title="Delete notification"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                    <h4 className="notification-title">{notification.title}</h4>
                                                    <p className="notification-message">{notification.message}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {notifications.length > 10 && (
                        <div className="notification-footer">
                            <button className="view-all-btn">
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
